import React from 'react';
import { cn } from '@/lib/utils';
import {
  computeZoneLayout,
  SPAN_STYLES,
} from './timeline/TimelinePrimitives';
import {
  TIMELINE_SEGMENT_HEADING_CLASS,
} from './timeline/timelineTextTokens';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface FloorBracket {
  /** Proportional duration weight (flex value) */
  flex: number;
  /** The prevailing (un-floored) rate multiplier at this bracket */
  prevailingMultiplier: number;
  /** Display label for the prevailing rate (e.g., "1.0x") */
  prevailingLabel: string;
}

interface FloorComparisonTimelineProps {
  /** Brackets from the un-floored base progression */
  brackets: FloorBracket[];
  /** The floor tier multiplier value (e.g., 1.5) */
  floorMultiplier: number;
  /** Display label for the floor (e.g., "1.5x") */
  floorLabel: string;
}

interface SpanRun {
  lifted: boolean;
  leftPct: number;
  rightPct: number;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const MERGED_HEIGHT = 56;
const SPLIT_HALF_HEIGHT = 28;
const SPAN_ROW_HEIGHT = 12;

const ZONE_TRANSITION = 'motion-safe:transition-[left,right,width,opacity] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]';

const MUTED_SPAN_STYLE = {
  bg: 'color-mix(in srgb, var(--color-foreground), transparent 97%)',
  border: 'color-mix(in srgb, var(--color-foreground), transparent 92%)',
  labelColor: 'color-mix(in srgb, var(--color-foreground), transparent 70%)',
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const FloorComparisonTimeline: React.FC<FloorComparisonTimelineProps> = ({
  brackets,
  floorMultiplier,
  floorLabel,
}) => {
  if (!Number.isFinite(floorMultiplier) || floorMultiplier < 0) return null;

  const validBrackets = brackets.filter(
    (b) => Number.isFinite(b.flex) && b.flex > 0 && Number.isFinite(b.prevailingMultiplier) && b.prevailingMultiplier >= 0,
  );
  if (validBrackets.length === 0) return null;

  const classified = validBrackets.map((b) => ({
    ...b,
    lifted: b.prevailingMultiplier < floorMultiplier,
  }));

  const hasLifted = classified.some((b) => b.lifted);
  // Higher minPct than other timelines (13) because span labels need room to be readable
  const { widths } = computeZoneLayout(classified.map((b) => ({ flex: b.flex })), { minPct: 22 });

  // Build contiguous runs of lifted/converged brackets for span annotations.
  // Adjacent brackets with the same classification merge into a single span.
  const runs: SpanRun[] = [];
  let cumPct = 0;
  for (let i = 0; i < classified.length; i++) {
    const prevRun = runs[runs.length - 1];
    const segEnd = cumPct + widths[i];
    if (prevRun && prevRun.lifted === classified[i].lifted) {
      prevRun.rightPct = 100 - segEnd;
    } else {
      runs.push({ lifted: classified[i].lifted, leftPct: cumPct, rightPct: 100 - segEnd });
    }
    cumPct = segEnd;
  }

  return (
    <div
      role="img"
      aria-label={`Floor comparison: ${floorLabel} guaranteed rate floor applied to rate progression`}
    >
        <div className="flex flex-col min-w-0">
          {/* Label */}
          <div className="text-center leading-none mb-2">
            <span className="block font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.5px] text-muted-foreground/60">
              Surcharge Period
            </span>
          </div>
          {/* Span annotations — one per contiguous run */}
          <div className="relative" style={{ height: SPAN_ROW_HEIGHT, marginBottom: 4 }}>
            {runs.map((run, i) => {
              const style = run.lifted ? SPAN_STYLES.v1 : MUTED_SPAN_STYLE;
              const label = run.lifted ? 'Surcharge' : 'No Surcharge';
              return (
                <div
                  key={i}
                  className={cn('absolute top-0 h-full flex items-center justify-center rounded-t-[0.25rem] overflow-hidden', ZONE_TRANSITION)}
                  style={{
                    left: `${run.leftPct}%`,
                    right: `${run.rightPct}%`,
                    background: style.bg,
                    borderTop: `1px solid ${style.border}`,
                    borderLeft: `1px solid ${style.border}`,
                    borderRight: `1px solid ${style.border}`,
                  }}
                >
                  <span
                    className="font-heading uppercase font-semibold whitespace-nowrap text-[10px] min-[375px]:text-[11px] sm:text-[12px] leading-none tracking-[0.75px] sm:tracking-[1px]"
                    style={{ color: style.labelColor }}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Timeline bars — group adjacent lifted brackets so adjusted half merges */}
          <div className="flex items-stretch">
            {(() => {
              // Build render groups: consecutive lifted brackets merge, converged are individual
              const groups: ({ type: 'lifted'; indices: number[] } | { type: 'converged'; index: number })[] = [];
              for (let i = 0; i < classified.length; i++) {
                if (classified[i].lifted) {
                  const lastGroup = groups[groups.length - 1];
                  if (lastGroup?.type === 'lifted') {
                    lastGroup.indices.push(i);
                  } else {
                    groups.push({ type: 'lifted', indices: [i] });
                  }
                } else {
                  groups.push({ type: 'converged', index: i });
                }
              }

              return groups.map((group, gi) => {
                const isLastGroup = gi === groups.length - 1;
                const isFirstGroup = gi === 0;

                if (group.type === 'lifted') {
                  const groupWidth = group.indices.reduce((s, idx) => s + widths[idx], 0);
                  const firstIdx = group.indices[0];
                  const lastIdx = group.indices[group.indices.length - 1];

                  return (
                    <div
                      key={`lift-${firstIdx}`}
                      className={cn(
                        'flex flex-col overflow-hidden',
                        'border border-border/50',
                        isFirstGroup && 'rounded-l-[0.25rem]',
                        isLastGroup && 'rounded-r-[0.25rem]',
                        ZONE_TRANSITION,
                      )}
                      style={{ width: `${groupWidth}%`, height: MERGED_HEIGHT }}
                    >
                      {/* Merged top half: surcharge-adjusted rate */}
                      <div
                        className="flex items-center justify-center bg-primary/[0.15]"
                        style={{
                          height: SPLIT_HALF_HEIGHT,
                          boxShadow: 'inset 0 0 12px color-mix(in srgb, var(--primary), transparent 92%)',
                        }}
                      >
                        <span className="relative inline-flex items-center justify-center">
                          <span className={cn(TIMELINE_SEGMENT_HEADING_CLASS, 'text-primary-text/95')}>
                            {floorLabel}
                          </span>
                          <span
                            aria-hidden="true"
                            className="pointer-events-none absolute right-full mr-1 text-primary-text/60 text-[10px] sm:text-[11px] leading-none"
                          >
                            ↑
                          </span>
                        </span>
                      </div>
                      {/* Horizontal divider */}
                      <div style={{ height: 0, borderTop: '1px dashed color-mix(in srgb, var(--primary), transparent 55%)' }} />
                      {/* Segmented bottom half: individual prevailing rates */}
                      <div className="flex items-stretch" style={{ height: SPLIT_HALF_HEIGHT }}>
                        {group.indices.map((idx, j) => (
                          <div
                            key={idx}
                            className={cn(
                              'flex items-center justify-center bg-foreground/[0.04]',
                              j > 0 && 'border-l border-l-border/30',
                              ZONE_TRANSITION,
                            )}
                            style={{ width: `${(widths[idx] / groupWidth) * 100}%` }}
                          >
                            <span className={cn(TIMELINE_SEGMENT_HEADING_CLASS, 'text-foreground/55 line-through')}>
                              {classified[idx].prevailingLabel}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Converged rendering — full-height merged block
                const i = group.index;
                const bracket = classified[i];
                const isHigherTier = bracket.prevailingMultiplier > floorMultiplier;
                return (
                  <div
                    key={`conv-${i}`}
                    className={cn(
                      'flex items-center justify-center',
                      isHigherTier ? 'bg-primary/[0.10]' : 'bg-primary/[0.06]',
                      'border border-border/50',
                      gi > 0 && 'border-l-0',
                      isLastGroup && 'rounded-r-[0.25rem]',
                      !hasLifted && isFirstGroup && 'rounded-l-[0.25rem]',
                      ZONE_TRANSITION,
                    )}
                    style={{ width: `${widths[i]}%`, height: MERGED_HEIGHT }}
                  >
                    <span className={cn(
                      TIMELINE_SEGMENT_HEADING_CLASS,
                      isHigherTier ? 'text-primary-text/85' : 'text-primary-text/65',
                    )}>
                      {bracket.prevailingLabel}
                    </span>
                  </div>
                );
              });
            })()}
          </div>

        </div>
    </div>
  );
};
